import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { HeroVisual } from '../components/HeroVisual';
import { ProductCard } from '../components/ProductCard';
import {
  SERVICES_CONFIG,
  PRODUCTS_CONFIG,
  PRINCIPLES_CONFIG,
  BUILD_STAGES_CONFIG,
  COMPANY_CONFIG,
} from '../config/site';

export function HomePage() {
  return (
    <>
      <SEOHead
        title="Safaza Technologies, Inc. | Software That Solves Real Business Problems"
        description="Safaza Technologies, Inc. designs, develops, and operates software products for businesses. We build custom web and mobile applications, SaaS platforms, and enterprise integrations."
      />

      {/* SECTION 1: HERO */}
      <section className="relative overflow-hidden pt-12 pb-20 sm:pt-20 sm:pb-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Left Column: Typography & CTAs */}
            <div className="lg:col-span-7 space-y-6 sm:space-y-8">
              {/* Category Eyebrow */}
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0D66E4]/8 border border-[#0D66E4]/20 text-[11px] font-mono font-medium text-[#0D66E4]">
                <span>SOFTWARE DEVELOPMENT &amp; SAAS PRODUCTS</span>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#101828] leading-[1.1] text-balance">
                Software that solves real business problems
                <span className="text-[#0D66E4]">.</span>
              </h1>

              {/* Supporting Text */}
              <p className="text-base sm:text-lg text-[#667085] leading-relaxed max-w-2xl">
                Safaza Technologies, Inc. designs, develops, and operates software products for
                businesses. From custom web and mobile applications to SaaS platforms and complex
                business systems, we turn practical ideas into reliable software.
              </p>

              {/* Capabilities Pill Badges */}
              <div className="flex flex-wrap gap-2 pt-1">
                {[
                  'Custom Software Development',
                  'Web & Mobile Applications',
                  'SaaS Products',
                  'API Integrations',
                  'Workflow Automation',
                  'Specialized Industry Software',
                ].map((cap, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 rounded-full bg-[#F6F8FB] border border-[#E4E7EC] text-xs font-medium text-[#475467]"
                  >
                    {cap}
                  </span>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                <Link
                  to="/services"
                  className="group inline-flex items-center justify-center gap-3 px-7 py-3.5 text-sm font-semibold text-white bg-[#101828] hover:bg-[#0D66E4] active:scale-[0.98] rounded-full transition-all duration-200 shadow-xs hover:shadow-md"
                >
                  <span>Explore our services</span>
                  <span className="w-5 h-5 rounded-full bg-white/10 group-hover:bg-white/20 flex items-center justify-center transition-transform duration-200 group-hover:translate-x-0.5">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </span>
                </Link>

                <Link
                  to="/products"
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-[#101828] hover:text-[#0D66E4] bg-[#F6F8FB] hover:bg-[#EFF6FF] border border-[#E4E7EC] hover:border-[#0D66E4]/30 rounded-full transition-all duration-200"
                >
                  <span>View SaaS products</span>
                  <svg
                    className="w-4 h-4 text-[#667085] group-hover:text-[#0D66E4] transition-transform duration-200 group-hover:translate-x-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Right Column: Hero Visual */}
            <div className="lg:col-span-5 flex justify-center">
              <HeroVisual />
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 2: BUSINESS MODEL & OPERATIONAL CLARITY */}
      <section className="py-16 sm:py-20 bg-[#F6F8FB] border-y border-[#E4E7EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#101828] mb-3">
              How Safaza Technologies Operates
              <span className="text-[#0D66E4]">.</span>
            </h2>
            <p className="text-base text-[#667085]">
              We operate a clear, dual-model technology business: providing bespoke software engineering
              services to clients and developing our own proprietary SaaS products.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl bg-white border border-[#E4E7EC] shadow-xs space-y-2">
              <div className="text-xs font-mono font-bold text-[#0D66E4] uppercase">What We Build</div>
              <h3 className="text-lg font-bold text-[#101828]">Software Systems</h3>
              <p className="text-xs sm:text-sm text-[#667085] leading-relaxed">
                Custom web applications, cross-platform mobile apps, cloud backends, API integrations, and internal business tools.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-[#E4E7EC] shadow-xs space-y-2">
              <div className="text-xs font-mono font-bold text-[#0D66E4] uppercase">Who We Serve</div>
              <h3 className="text-lg font-bold text-[#101828]">Commercial Clients</h3>
              <p className="text-xs sm:text-sm text-[#667085] leading-relaxed">
                Businesses, startups, and operational teams seeking dependable, scalable engineering without technical debt.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-[#E4E7EC] shadow-xs space-y-2">
              <div className="text-xs font-mono font-bold text-[#0D66E4] uppercase">Our Products</div>
              <h3 className="text-lg font-bold text-[#101828]">SaaS Platforms</h3>
              <p className="text-xs sm:text-sm text-[#667085] leading-relaxed">
                Proprietary software products such as Akoole (inventory &amp; business management) and modular cloud frameworks.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-[#E4E7EC] shadow-xs space-y-2">
              <div className="text-xs font-mono font-bold text-[#0D66E4] uppercase">How We Earn Revenue</div>
              <h3 className="text-lg font-bold text-[#101828]">Contracts &amp; SaaS</h3>
              <p className="text-xs sm:text-sm text-[#667085] leading-relaxed">
                Contractual engineering milestones, dedicated team retainers, ongoing software maintenance, and SaaS subscriptions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: SERVICES OVERVIEW */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 sm:mb-16">
            <div className="max-w-2xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#0D66E4] mb-2">
                Capabilities
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#101828] mb-4">
                Software engineering capabilities
                <span className="text-[#0D66E4]">.</span>
              </h2>
              <p className="text-base text-[#667085] leading-relaxed">
                From bespoke web and mobile applications to high-precision fintech software architectures,
                we deliver production-grade code that scales.
              </p>
            </div>

            <div>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#0D66E4] hover:text-[#0B56C2] group"
              >
                <span>View all services</span>
                <svg
                  className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {SERVICES_CONFIG.map((service) => (
              <div key={service.id} className="p-8 rounded-2xl bg-[#F6F8FB] border border-[#E4E7EC] hover:border-[#0D66E4]/40 transition-colors flex flex-col justify-between">
                <div>
                  <div className="inline-block px-2.5 py-0.5 rounded-full bg-[#0D66E4]/10 text-[#0D66E4] text-[11px] font-mono font-semibold mb-3">
                    {service.category}
                  </div>
                  <h3 className="text-xl font-bold text-[#101828] mb-2">
                    {service.title}
                  </h3>
                  <p className="text-sm font-medium text-[#667085] mb-3">
                    {service.tagline}
                  </p>
                  <p className="text-xs sm:text-sm text-[#475467] leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <div className="space-y-1.5 border-t border-[#E4E7EC] pt-4 mb-6">
                    {service.deliverables.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs text-[#667085]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#0D66E4]" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0D66E4] hover:underline"
                  >
                    <span>Read full specifications</span>
                    &rarr;
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: PRODUCTS SHOWCASE */}
      <section id="products-preview" className="py-24 sm:py-32 bg-[#F6F8FB] border-y border-[#E4E7EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 sm:mb-16">
            <div className="max-w-2xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#0D66E4] mb-2">
                Product Portfolio
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#101828] mb-4">
                SaaS products &amp; digital platforms
                <span className="text-[#0D66E4]">.</span>
              </h2>
              <p className="text-base text-[#667085] leading-relaxed">
                In addition to building software for clients, we develop and operate our own software
                products, including the Akoole inventory and business management platform.
              </p>
            </div>

            <div>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#0D66E4] hover:text-[#0B56C2] group"
              >
                <span>View all products</span>
                <svg
                  className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>
          </div>

          {/* Product Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {PRODUCTS_CONFIG.map((product) => (
              <ProductCard key={product.id} product={product} compact={true} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5: OUR APPROACH */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#101828] mb-4">
              Thoughtful by design. Built to last
              <span className="text-[#0D66E4]">.</span>
            </h2>
            <p className="text-base text-[#667085]">
              Three governing principles guide our software engineering and product development execution.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {PRINCIPLES_CONFIG.map((principle, index) => (
              <div
                key={principle.id}
                className="bg-[#F6F8FB] rounded-2xl p-8 border border-[#E4E7EC] shadow-xs flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-xl bg-[#0D66E4]/10 text-[#0D66E4] flex items-center justify-center font-mono font-bold text-sm mb-6">
                    0{index + 1}
                  </div>
                  <h3 className="text-xl font-bold text-[#101828] mb-3">
                    {principle.title}
                  </h3>
                  <p className="text-sm text-[#667085] leading-relaxed">
                    {principle.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6: HOW WE BUILD */}
      <section className="py-24 sm:py-32 bg-[#F6F8FB] border-y border-[#E4E7EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#101828] mb-4">
              From workflow to production software
              <span className="text-[#0D66E4]">.</span>
            </h2>
            <p className="text-base text-[#667085]">
              A disciplined, engineering-first execution method ensuring sound architecture at every phase.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BUILD_STAGES_CONFIG.map((stage) => (
              <div
                key={stage.step}
                className="p-6 rounded-2xl bg-white border border-[#E4E7EC] relative group hover:border-[#0D66E4]/40 transition-colors shadow-xs"
              >
                <span className="text-xs font-mono font-semibold text-[#0D66E4] block mb-3">
                  STAGE 0{stage.step}
                </span>
                <h3 className="text-lg font-bold text-[#101828] mb-2">
                  {stage.title}
                </h3>
                <p className="text-sm text-[#667085] leading-relaxed">
                  {stage.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7: CORPORATE SCOPE & ABOUT PREVIEW */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="double-bezel max-w-4xl mx-auto">
            <div className="double-bezel-inner p-8 sm:p-12 md:p-16">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#0D66E4] mb-3">
                Corporate Overview
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#101828] mb-6">
                Engineered in the United States. Built for scale
                <span className="text-[#0D66E4]">.</span>
              </h2>
              <p className="text-base sm:text-lg text-[#667085] leading-relaxed mb-6">
                {COMPANY_CONFIG.legalName} is a software engineering company incorporated in the United
                States. We develop custom software for business clients and build or operate proprietary
                SaaS platforms. Our technical team brings proven domain expertise in software systems,
                including financial technology workflows, APIs, and business data platforms.
              </p>
              <p className="text-xs sm:text-sm text-[#98A2B3] leading-relaxed mb-8">
                Safaza Technologies, Inc. provides software development services and technology products;
                it does not provide banking, money transmission, payment processing, or custodial financial
                services.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-[#101828] hover:bg-[#0D66E4] rounded-full transition-colors"
                >
                  <span>Read our corporate profile</span>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-[#101828] hover:text-[#0D66E4] bg-[#F6F8FB] border border-[#E4E7EC] rounded-full transition-colors"
                >
                  <span>Explore capabilities</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8: CONTACT CTA */}
      <section className="py-20 sm:py-28 bg-[#F6F8FB] border-t border-[#E4E7EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#101828] mb-4">
              Have a software project or business inquiry?
            </h2>
            <p className="text-base text-[#667085] leading-relaxed mb-8">
              Get in touch with Safaza Technologies to discuss your software engineering needs,
              explore our SaaS products, or initiate a commercial partnership.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 text-base font-semibold text-white bg-[#0D66E4] hover:bg-[#0B56C2] active:scale-[0.98] rounded-full transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <span>Contact our team</span>
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

